import type { VehicleValuePoint } from "@gladis/shared";
import { StackedAreaChart, type ChartSeries } from "@gladis/ui";
import { useT } from "@gladis/ui/i18n";
import type { ReactNode } from "react";

/**
 * What the household's vehicles have been OBSERVED to be worth — one line, not a stack.
 *
 * ==========================================================================================
 * `lines`, FOR THE REASON THE PORTFOLIO CHART GIVES
 * ==========================================================================================
 *
 * A stack composes, so its top is the whole; lines compare, so each is its own value. There is
 * exactly one series here, and a stacked chart of one series draws the same shape while implying a
 * decomposition that does not exist. Per-vehicle layers would be a deliberate decision to revisit,
 * not one to inherit.
 *
 * ==========================================================================================
 * A FLAT LINE IS THE HONEST SHAPE, AND IT IS WHY THIS CHART LOOKS UNLIKE THE OTHERS
 * ==========================================================================================
 *
 * Savings moves every month because a balance is recorded every month. Investments moves every day
 * because a price is fetched. A vehicle moves when somebody looks it up — three times a year at
 * best — so this line is a STAIRCASE, flat between observations and stepping at each one.
 *
 * That is not missing data. `docs/vehicles-decisions.md` §4.2 reversed a per-type depreciation curve
 * precisely so this chart could not draw a smooth decline nobody measured: there is no free
 * market-value source for a car, so a curve would restate the household's own last entry with a
 * number stapled to it, and then render it to the cent. A staircase says "this is what we know";
 * a curve would say "this is what it is worth", which the application cannot know.
 *
 * The empty message covers a household that owns vehicles and has valued none — the series starts at
 * the first OBSERVATION, so a household with no observations has no line rather than a flat run
 * along zero. Zero is a claim; absence is not.
 */
export function VehicleValueChart({ points }: { points: readonly VehicleValuePoint[] }): ReactNode {
  const t = useT();

  const series: ChartSeries[] = [
    {
      id: "vehicles",
      label: t("veh.chart.series"),
      points: points.map((point) => ({ at: point.month, value: point.valueInEuros })),
    },
  ];

  return <StackedAreaChart series={series} variant="lines" emptyMessage={t("veh.chart.empty")} />;
}
